/* ============================================================
   Anzeigeformate. Die Zeichenketten der Oberfläche stehen in texte.ts; hier
   stehen die Formatierer, die beide Sprachen bedienen.

   Drei Regeln, die hier durchgehalten werden:

   1. DER TAG IST EIN KALENDERTAG, KEIN ZEITPUNKT. Ein Abgabetermin ist
      „2026-09-21", nicht „2026-09-21T00:00:00Z". Wer daraus ein Date macht
      und mit `Date.now()` verrechnet, bekommt je nach Zeitzone und Sommerzeit
      einen Tag zu viel oder zu wenig — und genau das ist die Zahl, die als
      „in 4 T" auf der Karte steht. Deshalb wird die Differenz aus den
      Kalenderfeldern gerechnet (UTC-Mitternacht beider Tage), und „heute"
      kommt über Europe/Berlin (SPEC §4), nicht aus der Uhr des Geräts.
   2. Deutsches Dezimalkomma im Deutschen, Punkt im Englischen — beides über
      `Intl.NumberFormat`, nicht über `String.replace`.
   3. Ein unlesbares Datum ergibt "" und niemals „Invalid Date".

   Zu Regel 1 gehört die Kehrseite: ein ZEITPUNKT („2026-09-01T22:30:00+00:00")
   ist kein Kalendertag. Der Server schickt Zeitpunkte in UTC; ihr Tag ist der
   Berliner, nicht die ersten zehn Zeichen (Fehlerrunde 01.10.2026, #27).
   ============================================================ */
import type { Sprache } from "../api/types";

const LOCALE: Record<Sprache, string> = { de: "de-DE", en: "en-GB" };
const ZEITZONE = "Europe/Berlin";

/* ---------------------------------------------------------------- Kalender */

/**
 * Ein Zeitpunkt MIT Zonenangabe, wie der Server ihn schickt:
 * „2026-09-01T22:30:00Z", „2026-09-01T22:30:00.123456+00:00", „… 22:30+0200".
 * Nur diese Form wird in Berlin gelesen — ein Zeitpunkt ohne Zone sagt nicht,
 * wessen Uhr er meint, und behält darum seine eigenen ersten zehn Zeichen.
 */
const MIT_ZONE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:[.,]\d+)?)?\s*(?:Z|[+-]\d{2}(?::?\d{2})?)$/i;

/**
 * "2026-09-21" → [Jahr, Monat (1–12), Tag]; ein Zeitpunkt mit Zone
 * („2026-09-21T22:30:00Z") → die Felder seines BERLINER Kalendertags.
 *
 * Fehlerrunde 01.10.2026, #27: vorher galten auch bei einem Zeitpunkt die ersten
 * zehn Zeichen — das ist der UTC-Tag, und alles zwischen Mitternacht und 2 Uhr
 * Berliner Zeit stand am Vortag. Lässt sich der Zeitpunkt nicht lesen, bleibt
 * es beim alten Weg (die ersten zehn Zeichen) statt bei gar nichts.
 */
function kalenderfelder(iso: string): [number, number, number] | null {
  const roh = iso.trim();
  const tag = MIT_ZONE.test(roh) ? berlinTag(roh) || roh : roh;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(tag);
  if (!m) return null;
  const j = Number(m[1]);
  const mo = Number(m[2]);
  const t = Number(m[3]);
  if (!Number.isFinite(j) || !Number.isFinite(mo) || !Number.isFinite(t)) return null;
  return [j, mo, t];
}

/** Heutiges Datum in Europe/Berlin als "YYYY-MM-DD". */
export function heuteIso(jetzt: Date = new Date()): string {
  // `en-CA` liefert von sich aus YYYY-MM-DD; das ist der kürzeste Weg zu den
  // Kalenderfeldern einer fremden Zeitzone, ohne eine Bibliothek zu laden.
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone: ZEITZONE }).format(jetzt);
  } catch {
    return jetzt.toISOString().slice(0, 10);
  }
}

/**
 * Der Berliner Kalendertag eines Zeitpunkts ("2026-09-27T22:30:00Z" → "2026-09-28").
 *
 * Warum nicht einfach die ersten zehn Zeichen: der Server schickt Zeitpunkte
 * in UTC, und alles zwischen Mitternacht und 2 Uhr Berliner Zeit stünde sonst
 * am Vortag. Ein bloßes Datum bleibt, wie es ist. Unlesbares → "" (Regel 3) —
 * und zwar VOR `heuteIso`, dessen Rückweg über `toISOString` bei einem
 * ungültigen Datum würfe.
 *
 * Stand bis zur Fehlerrunde 01.10.2026 (#27) nur in der Zeitung; seitdem hier,
 * damit jede Anzeige eines Zeitpunkts denselben Tag nennt.
 */
export function berlinTag(zeitpunkt: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(zeitpunkt)) return zeitpunkt;
  const t = Date.parse(zeitpunkt);
  if (!Number.isFinite(t)) return "";
  return heuteIso(new Date(t));
}

/**
 * Tage von heute (Europe/Berlin) bis zum genannten Tag.
 * Positiv = in der Zukunft, 0 = heute, negativ = vorbei.
 * Ein unlesbares Datum ergibt `null` — der Aufrufer zeigt dann gar nichts,
 * statt „NaN T".
 */
export function tageBis(iso: string, jetzt: Date = new Date()): number | null {
  const ziel = kalenderfelder(iso);
  const heute = kalenderfelder(heuteIso(jetzt));
  if (!ziel || !heute) return null;
  const a = Date.UTC(heute[0], heute[1] - 1, heute[2]);
  const b = Date.UTC(ziel[0], ziel[1] - 1, ziel[2]);
  return Math.round((b - a) / 86_400_000);
}

/** Fällig und noch nicht abgegeben? Die Regel aus SPEC §4, als eine Zeile. */
export function istVerspaetet(faellig: string, abgegeben: boolean, jetzt: Date = new Date()): boolean {
  if (abgegeben) return false;
  const n = tageBis(faellig, jetzt);
  return n !== null && n < 0;
}

/* ------------------------------------------------------------------ Datum */

function alsDate(iso: string): Date | null {
  const f = kalenderfelder(iso);
  if (!f) return null;
  // Mittags in UTC: so kippt die Anzeige auch bei einer Zeitzone bis ±11 h
  // nicht auf den Nachbartag.
  return new Date(Date.UTC(f[0], f[1] - 1, f[2], 12));
}

/** "21.09.2026" (de) · "21 Sep 2026" (en). */
export function datum(iso: string, sprache: Sprache): string {
  const d = alsDate(iso);
  if (!d) return "";
  return new Intl.DateTimeFormat(LOCALE[sprache], { day: "2-digit", month: sprache === "de" ? "2-digit" : "short", year: "numeric", timeZone: "UTC" }).format(d);
}

/**
 * "21.09." (de) · "21 Sep" (en) — für Zeilen, in denen das Jahr aus dem
 * Zusammenhang klar ist.
 *
 * Der deutsche Zweig setzt die Zahlen selbst zusammen und ruft NICHT
 * `Intl.DateTimeFormat`: dessen deutsche Kurzform trägt den Schlusspunkt je
 * nach Browserfassung mal mit und mal ohne, und aus „21.09." würde dann
 * „21.09.." oder „21.09". Zwei Stellen, ein Punkt, kein Zweifel.
 */
export function datumKurz(iso: string, sprache: Sprache): string {
  const f = kalenderfelder(iso);
  if (!f) return "";
  if (sprache === "de") return `${String(f[2]).padStart(2, "0")}.${String(f[1]).padStart(2, "0")}.`;
  const d = alsDate(iso);
  if (!d) return "";
  return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", timeZone: "UTC" }).format(d);
}

/** "Do 17.09.2026" (de) · "Thu 17 Sep 2026" (en) — die Heute-Marke der Zeitleiste. */
export function datumMitTag(iso: string, sprache: Sprache): string {
  const d = alsDate(iso);
  if (!d) return "";
  const wt = new Intl.DateTimeFormat(LOCALE[sprache], { weekday: "short", timeZone: "UTC" }).format(d);
  return `${wt} ${datum(iso, sprache)}`;
}

/** "September 2026" — Überschrift einer Monatsgruppe. */
export function monatJahr(iso: string, sprache: Sprache): string {
  const d = alsDate(iso);
  if (!d) return "";
  return new Intl.DateTimeFormat(LOCALE[sprache], { month: "long", year: "numeric", timeZone: "UTC" }).format(d);
}

/**
 * Zeitstempel als Abstand zu jetzt: „vor 2 Std.", „gestern", „vor 3 Tagen",
 * ab 14 Tagen das Datum (des Berliner Tags). `Intl.RelativeTimeFormat` macht daraus von selbst
 * die richtige Form in beiden Sprachen.
 */
export function relativZeit(iso: string, sprache: Sprache, jetzt: Date = new Date()): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const s = Math.max(0, (jetzt.getTime() - t) / 1000);
  const rtf = new Intl.RelativeTimeFormat(LOCALE[sprache], { numeric: "auto" });
  // numeric: "auto" macht daraus „jetzt" bzw. „now" — keine „vor 0 Sekunden".
  if (s < 45) return rtf.format(0, "second");
  if (s < 3600) return rtf.format(-Math.round(s / 60), "minute");
  if (s < 86_400) return rtf.format(-Math.round(s / 3600), "hour");
  const tage = Math.round(s / 86_400);
  if (tage < 14) return rtf.format(-tage, "day");
  // Der Berliner Tag des schon gelesenen Zeitpunkts, nicht `iso.slice(0, 10)`:
  // das wäre der UTC-Tag, und ein Bild von 0:30 Uhr stünde am Vortag
  // (Fehlerrunde 01.10.2026, #27).
  return datum(heuteIso(new Date(t)), sprache);
}

/** Restsekunden als „0:41" — für „Neuen Code in …". */
export function sekundenKurz(s: number): string {
  const ganz = Math.max(0, Math.ceil(s));
  return `${Math.floor(ganz / 60)}:${String(ganz % 60).padStart(2, "0")}`;
}

/* --------------------------------------------------------- Projekttitel */

/**
 * Die drei Teile, aus denen ein Projekttitel besteht. Absichtlich ein loser
 * Satz Felder und nicht `ProjektKurz`: dieselbe Funktion bedient `Projekt`,
 * `ProjektKurz`, den `Stift` der Karte und jede handgebaute Zeile.
 */
export interface Titelteile {
  kuerzel?: string | null;
  nummer?: string | null;
  name: string;
}

/**
 * Das Mono-Präfix eines Projekttitels: „KUE 2026-017" — Kürzel und Nummer,
 * jeder Teil nur wenn er da ist. Ohne beides ist es "".
 *
 * Es steht getrennt, weil die Oberfläche es GETRENNT setzt: in der Akte als
 * kleine Monozeile über dem Namen, auf einer Kachel als blasse Vorsilbe. Wer
 * beides in einem Stück braucht, nimmt `projektTitel()`.
 */
export function projektPraefix(p: Titelteile): string {
  return [p.kuerzel, p.nummer]
    .map((x) => (x ?? "").trim())
    .filter((x) => x.length > 0)
    .join(" ");
}

/**
 * Der Projekttitel, wie er überall gleich aussehen muss (SPEC §11.1):
 * `[Kürzel] [Nummer] · Name`. Fehlende Teile fallen weg, ohne beides bleibt
 * der blanke Name — und niemals ein einsames „ · " davor.
 *
 * Sortiert und gesucht wird weiterhin auf `name` (die Suche trifft zusätzlich
 * `kuerzel`); dies hier ist reine ANZEIGE.
 */
export function projektTitel(p: Titelteile): string {
  const praefix = projektPraefix(p);
  const name = p.name.trim();
  if (!praefix) return name;
  if (!name) return praefix;
  return `${praefix} · ${name}`;
}

/* ------------------------------------------------------------------ Zahlen */

/**
 * Fläche. Die Datenbank liefert Quadratmeter (geodätisch gerechnet, T9);
 * angezeigt wird unter einem Hektar in m², darüber in ha mit zwei
 * Nachkommastellen — so steht es in der Entwurfsmappe („2,84 ha").
 */
export function flaeche(m2: number | null | undefined, sprache: Sprache): string {
  if (m2 === null || m2 === undefined || !Number.isFinite(m2)) return "";
  const nf = (min: number, max: number) => new Intl.NumberFormat(LOCALE[sprache], { minimumFractionDigits: min, maximumFractionDigits: max });
  if (m2 < 10_000) return `${nf(0, 0).format(Math.round(m2))} m²`;
  return `${nf(2, 2).format(m2 / 10_000)} ha`;
}

/** Eine Zahl mit Tausendertrennung der jeweiligen Sprache. */
export function zahl(n: number, sprache: Sprache, nachkomma = 0): string {
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat(LOCALE[sprache], { minimumFractionDigits: nachkomma, maximumFractionDigits: nachkomma }).format(n);
}

/**
 * Ein Betrag in Euro (015, SPEC §18.3): „120.000 €", ohne Nachkommastellen,
 * solange keine anfallen.
 *
 * `null` und `undefined` geben LEER und nicht „0 €" — die beiden Fälle heißen
 * „nicht erfasst" und „kein Recht" (types.ts), und beide sind etwas anderes als
 * ein Auftrag über null Euro. Welcher der beiden vorliegt, entscheidet die
 * Ansicht mit `darfGeldSehen`; hier wird nur gerechnet.
 */
export function euro(n: number | null | undefined, sprache: Sprache): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "";
  const nachkomma = Number.isInteger(n) ? 0 : 2;
  return new Intl.NumberFormat(LOCALE[sprache], {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: nachkomma,
    maximumFractionDigits: nachkomma,
  }).format(n);
}

/**
 * Was jemand in ein Betragsfeld tippt, als Zahl — oder `null` fürs Leeren.
 *
 * Gelesen wird beides: „120000", „120.000", „120.000,50" und „120,000.50".
 * Die Regel dafür ist einfach und deckt jede Schreibweise ab, die im Haus
 * vorkommt: **das LETZTE Trennzeichen ist das Komma**, alles davor ist
 * Tausendertrennung. Ohne diese Regel wäre „120.000" je nach Sprache ein
 * Auftrag über hundertzwanzigtausend oder über hundertzwanzig Euro.
 *
 * `NaN` gibt `undefined`: der Aufrufer schickt dann gar nichts, statt ein
 * gesetztes Honorar mit einem Tippfehler zu leeren.
 */
export function betragLesen(text: string): number | null | undefined {
  const roh = text.replace(/[\s€]/g, "");
  if (roh === "") return null;
  const letztes = Math.max(roh.lastIndexOf(","), roh.lastIndexOf("."));
  if (letztes < 0) {
    const n = Number(roh);
    return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : undefined;
  }
  const vorne = roh.slice(0, letztes).replace(/[.,]/g, "");
  const hinten = roh.slice(letztes + 1);
  // Zwei VERSCHIEDENE Trennzeichen heißen: das letzte ist das Komma
  // („120.000,50" und „120,000.50"). Lauter gleiche mit drei Stellen dahinter
  // sind Tausender („1.234.567"). Und eine einzelne Trennung mit drei Stellen
  // dahinter ist die häufigste Schreibweise im Haus: „120.000" sind
  // hundertzwanzigtausend Euro, nicht hundertzwanzig.
  const arten = new Set(roh.split("").filter((c) => c === "." || c === ","));
  const tausender = hinten.length === 3 && arten.size <= 1;
  const n = Number(tausender ? vorne + hinten : vorne + "." + (hinten || "0"));
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.round(n * 100) / 100;
}

/** Dateigröße für die Galerie und die Grenz-Aufnahme: „4,2 MB". */
export function bytes(n: number, sprache: Sprache): string {
  if (!Number.isFinite(n) || n < 0) return "";
  if (n < 1024) return `${n} B`;
  const einheiten = ["kB", "MB", "GB"];
  let wert = n / 1024;
  let i = 0;
  while (wert >= 1024 && i < einheiten.length - 1) {
    wert /= 1024;
    i += 1;
  }
  return `${zahl(wert, sprache, wert < 10 ? 1 : 0)} ${einheiten[i]}`;
}
